import React, { useState } from 'react';
import { Card, Button, Select, Row, Col, Space, Statistic, Radio, message, DatePicker } from 'antd';
import ReactECharts from 'echarts-for-react';
import USMapChart from '../../../components/Charts/USMapChart';
import dayjs from 'dayjs';
import api from '../../../services/api';
import { getTrendOption, getAOVTrendOption, getHourOption, getWeekOption, getHeatmapOption, getGeoOption } from './chartConfig';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface Props {
    shopId: number | null;
}

const AnalysisDashboardTab: React.FC<Props> = ({ shopId }) => {
    // Analysis Data State
    const [trendData, setTrendData] = useState<any[]>([]);
    const [distData, setDistData] = useState<any>(null);
    const [geoData, setGeoData] = useState<any>(null);

    // Analysis UI State
    const [dates, setDates] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>([dayjs().year(2025).startOf('year'), dayjs().year(2025).endOf('year')]);
    const [analysisLoading, setAnalysisLoading] = useState(false);
    const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('week');

    // Filter State
    const [country, setCountry] = useState<string>('US');
    const [salesChannel, setSalesChannel] = useState<string>('Amazon.com');
    const [countries, setCountries] = useState<string[]>([]);
    const [salesChannels, setSalesChannels] = useState<string[]>([]);

    React.useEffect(() => {
        if (shopId) {
            // Fetch filter options
            api.get('/data-analysis/filter-options/', { params: { shop_id: shopId } })
                .then(res => {
                    setCountries(res.data.countries || []);
                    setSalesChannels(res.data.sales_channels || []);
                })
                .catch(err => console.error('Failed to load filter options:', err));
        }
    }, [shopId]);

    const loadAnalysis = async () => {
        if (!dates || !dates[0] || !dates[1]) {
            message.warning('请选择日期范围');
            return;
        }

        if (!shopId) {
            message.warning('请先选择店铺');
            return;
        }

        setAnalysisLoading(true);
        try {
            const commonParams = {
                start_date: dates[0].format('YYYY-MM-DD'),
                end_date: dates[1].format('YYYY-MM-DD'),
            };

            const [trendRes, distRes, geoRes] = await Promise.all([
                api.get('/data-analysis/sales-trend/', {
                    params: {
                        ...commonParams,
                        target_ids: String(shopId),
                        dimension: 'shop',
                        granularity,
                        country: country || undefined,
                        sales_channel: salesChannel || undefined
                    }
                }),
                api.get('/data-analysis/time-distribution/', {
                    params: { ...commonParams, shop_id: String(shopId) }
                }),
                api.get('/data-analysis/geographic-distribution/', {
                    params: { ...commonParams, shop_id: String(shopId), country: country || 'US' }
                })
            ]);

            setTrendData(trendRes.data);
            setDistData(distRes.data);
            setGeoData(geoRes.data);
            message.success('分析完成');
        } catch (error) {
            console.error(error);
            message.error('加载分析数据失败');
        } finally {
            setAnalysisLoading(false);
        }
    };

    return (
        <div>
            <Space style={{ marginBottom: 16 }}>
                <RangePicker
                    onChange={(dates) => setDates(dates as any)}
                    value={dates}
                    presets={[
                        { label: '过去7天', value: [dayjs().subtract(7, 'd'), dayjs()] },
                        { label: '过去30天', value: [dayjs().subtract(30, 'd'), dayjs()] },
                        { label: '过去90天', value: [dayjs().subtract(90, 'd'), dayjs()] },
                        { label: '本月', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                        { label: '今年', value: [dayjs().startOf('year'), dayjs().endOf('year')] },
                        { label: '去年', value: [dayjs().subtract(1, 'year').startOf('year'), dayjs().subtract(1, 'year').endOf('year')] },
                        { label: '全周期 (3年)', value: [dayjs().subtract(3, 'year'), dayjs()] },
                    ]}
                />
                <Radio.Group
                    value={granularity}
                    onChange={(e) => setGranularity(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                >
                    <Radio.Button value="day">按天</Radio.Button>
                    <Radio.Button value="week">按周</Radio.Button>
                    <Radio.Button value="month">按月</Radio.Button>
                </Radio.Group>
                <Select
                    placeholder="国家/地区"
                    style={{ width: 120 }}
                    allowClear
                    value={country || undefined}
                    onChange={(val) => setCountry(val || '')}
                >
                    {countries.map(c => <Option key={c} value={c}>{c}</Option>)}
                </Select>
                <Select
                    placeholder="销售渠道"
                    style={{ width: 160 }}
                    allowClear
                    value={salesChannel || undefined}
                    onChange={(val) => setSalesChannel(val || '')}
                >
                    {salesChannels.map(c => <Option key={c} value={c}>{c}</Option>)}
                </Select>
                <Button type="primary" onClick={loadAnalysis} loading={analysisLoading}>分析</Button>
            </Space>
            <Row gutter={16}>
                {/* 汇总统计卡片 */}
                <Col span={24} style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                        <Col span={6}>
                            <Card size="small">
                                <Statistic
                                    title="订单数"
                                    value={trendData.reduce((sum, d) => sum + (d.order_items || d.sessions || 0), 0)}
                                    suffix="单"
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small">
                                <Statistic
                                    title="销量"
                                    value={trendData.reduce((sum, d) => sum + (d.units || 0), 0)}
                                    suffix="件"
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small">
                                <Statistic
                                    title="销售额"
                                    value={trendData.reduce((sum, d) => sum + (d.sales || 0), 0).toFixed(2)}
                                    prefix="$"
                                />
                            </Card>
                        </Col>
                        <Col span={6}>
                            <Card size="small">
                                <Statistic
                                    title="客单价"
                                    value={
                                        trendData.reduce((sum, d) => sum + (d.order_items || d.sessions || 0), 0) > 0
                                            ? (trendData.reduce((sum, d) => sum + (d.sales || 0), 0) / trendData.reduce((sum, d) => sum + (d.order_items || d.sessions || 0), 0)).toFixed(2)
                                            : '0.00'
                                    }
                                    prefix="$"
                                />
                            </Card>
                        </Col>
                    </Row>
                </Col>
                <Col span={24} style={{ marginBottom: 16 }}>
                    <Card size="small">
                        <ReactECharts option={getTrendOption(trendData, granularity)} style={{ height: 350 }} />
                    </Card>
                </Col>
                <Col span={24} style={{ marginBottom: 16 }}>
                    <Card size="small">
                        <ReactECharts option={getAOVTrendOption(trendData, granularity)} style={{ height: 300 }} />
                    </Card>
                </Col>
                <Col span={12} style={{ marginBottom: 16 }}>
                    <Card size="small">
                        <ReactECharts option={getHourOption(distData)} style={{ height: 300 }} />
                    </Card>
                </Col>
                <Col span={12} style={{ marginBottom: 16 }}>
                    <Card size="small">
                        <ReactECharts option={getWeekOption(distData)} style={{ height: 300 }} />
                    </Card>
                </Col>
                <Col span={24}>
                    <Card size="small" bodyStyle={{ padding: '16px 8px' }}>
                        <ReactECharts option={getHeatmapOption(distData)} style={{ height: 500 }} />
                    </Card>
                </Col>
                <Col span={24} style={{ marginTop: 24 }}>
                    <Card title="地理分布">
                        <Row gutter={16}>
                            <Col span={14}>
                                <USMapChart data={geoData?.states || []} country={geoData?.country || 'US'} />
                            </Col>
                            <Col span={10}>
                                <ReactECharts option={getGeoOption(geoData)} style={{ height: 500 }} />
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default AnalysisDashboardTab;
